<template>
    <v-card
        class="products-labels"
        outlined
    >
        <v-card-title class="page-heading primary">
            <div class="wfull d-flex justify-space-between">
                <div class="d-flex align-center">
                    <v-btn
                        color="white"
                        icon
                        @click="goBack"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>
                    <div class="ml-3">{{ $t('general.price_tags_print') }}</div>
                </div>

                <div>
                    <v-btn
                        class="minw100 mr-3"
                        color="white"
                        outlined
                        @click="print"
                    >
                        {{ $t('general.print') }}
                    </v-btn>

                    <v-menu bottom transition="slide-y-transition">
                        <template v-slot:activator="{ on }">
                            <v-btn dark v-on="on" icon>
                                <v-icon>far fa-ellipsis-v</v-icon>
                            </v-btn>
                        </template>

                        <v-list>
                            <v-list-item
                                :key="1"
                                :disabled="show === 'all'"
                                @click="show = 'all'"
                            >
                                <v-list-item-action class="mx-3">
                                    <v-icon :disabled="show === 'all'">fal fa-list-alt</v-icon>
                                </v-list-item-action>
                                <v-list-item-title>{{ $t('general.show_all') }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                                :key="2"
                                :disabled="show === 'custom'"
                                @click="show = 'custom'"
                            >
                                <v-list-item-action class="mx-3">
                                    <v-icon :disabled="show === 'custom'">fal fa-search</v-icon>
                                </v-list-item-action>
                                <v-list-item-title>{{ $tc('general.select_item', 2) }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                                :key="3"
                                :disabled="show === 'recent'"
                                @click="show = 'recent'"
                            >
                                <v-list-item-action class="mx-3">
                                    <v-icon :disabled="show === 'recent'">fal fa-clock</v-icon>
                                </v-list-item-action>
                                <v-list-item-title>{{ $t('products.updated_in_24h') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
        </v-card-title>

        <v-card-text class="page-content">
            <v-row class="table-settings">
                <v-col cols="12" sm="6">
                    <v-slider
                        v-model="cellHeight"
                        class="mx-2"
                        :label="$t('general.price_tags_height')"
                        max="200"
                        min="50"
                        dense
                        hide-details
                        thumb-label
                    />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-slider
                        v-model="productsPerRow"
                        class="mx-2"
                        :label="$t('general.price_tags_per_row')"
                        max="8"
                        min="2"
                        step="1"
                        dense
                        hide-details
                        thumb-label
                    />
                </v-col>
            </v-row>
            <div class="d-flex justify-end">
                <search-product
                    v-if="show === 'custom'"
                    ref="searchProduct"
                    class="my-5 flex-1"
                    hide-details
                    :preserve-selection="false"
                    :disable-quantity="true"
                    :products="allProducts"
                    @select="selectProduct($event.product, $event.quantity)"
                />
                <v-pagination
                    v-model="page"
                    class="my-4 ml-4"
                    :length="totalPages"
                    :total-visible="10"
                />
            </div>
            <table id="price-tags" style="width: 100%; border-collapse: collapse; table-layout: fixed;">
                <tbody>
                    <tr
                        v-for="(productRow, rowIndex) in products"
                        :key="'row'+rowIndex"
                        style="break-inside: avoid"
                    >
                        <td
                            v-for="(product, colIndex) in productRow"
                            :key="'col'+colIndex"
                            style="padding: 4px; text-align: center; border: 1px solid lightgrey; color: black;"
                        >
                            <div
                                :style="{
                                    height: cellHeight + 'px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }"
                            >
                                <div style="font-weight: 500; font-size: 12px; text-align: center; line-height: 1; padding: 3px 0;">
                                    <div style="transform: scale(1, 1.5);">{{ product.name }}</div>
                                </div>
                                <div style="display: flex; align-items: center; flex-direction: column; width: 100%;">
                                    <div v-if="product.selling_price" style="height: 45px; font-weight: 600; display: flex; align-items: center; margin: 6px 0;">
                                        <div v-if="Money.currency === 'USD'" style="font-size: 16px; margin-bottom: 25px; transform: scale(1, 1.4);">
                                            {{ currencySymbol }}
                                        </div>
                                        <div style="font-size: 32px; transform: scale(1, 1.5);">
                                            {{ getPriceWoCents(product.getPriceAfterTax().toFormat('0.00')) }}
                                        </div>
                                        <div style="text-align: left; display: flex; flex-direction: column; justify-content: space-between; height: 100%;">
                                            <div style="font-size: 17px; transform: scale(1, 1.5);">
                                                {{ getCents(product.getPriceAfterTax().toFormat('0.00')) }}
                                            </div>
                                            <div style="margin-left: 2px;">
                                                <div v-if="Money.currency !== 'USD' && currencySymbol.length > 1" style="font-size: 13px; transform: scale(1, 1.4);">
                                                    {{ currencySymbol }}
                                                </div>
                                                <div v-if="Money.currency !== 'USD' && currencySymbol.length === 1" style="font-size: 18px;">
                                                    {{ currencySymbol }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="product.barcode" style="display: flex; justify-content: center; width: 100%;">
                                    <div style="display: flex; margin-bottom: 0px;">
                                        <vue-barcode
                                            :value="product.barcode"
                                            width="1"
                                            height="16"
                                            font-size="11x"
                                            :text-margin="0"
                                            :margin="0"
                                        >
                                            barcode error
                                        </vue-barcode>
                                    </div>
                                </div>
                                <div style="display: flex; justify-content: space-between; width: 100%;">
                                    <div style="font-size: 9px; transform: scale(1, 1.3); text-align: right;">
                                        <span v-if="product.code">{{ product.code }}</span>
                                    </div>
                                    <div style="font-size: 9px; transform: scale(1, 1.3); display: flex; justify-content: flex-end;">
                                        <div v-if="product.getNetQuantity() && product.unit_of_measure" style="display: flex;">
                                            <div v-if="Money.currency === 'USD'">{{ currencySymbol }}</div>
                                            <div>{{ product.getQuantityUnitPrice().toFormat('0.00') }}</div>
                                            <div v-if="Money.currency !== 'USD'">&nbsp;{{ currencySymbol }}</div>
                                            <div>/{{ product.unit_of_measure.toUpperCase() }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td v-for="i in productsPerRow - productRow.length" :key="i"></td>
                    </tr>
                </tbody>
            </table>
        </v-card-text>
    </v-card>
</template>

<script>
import SearchProduct from '@/views/shared/components/SearchProduct';
import Product from '@/models/Product';
import { loadData } from '@/views/shared/loadPageData';
import VueBarcode from 'vue-barcode';
import Money from '@/wrappers/Money'

const PRICE_TAGS_HEIGHT = 'price-tags-height';
const PRICE_TAGS_PER_ROW = 'price-tags-per-row';

export default {
    components: {
        VueBarcode,
        SearchProduct
    },

    props: {
    },

    created() {
        const cellHeight = AppStorage.local().getItem(PRICE_TAGS_HEIGHT)
        const productsPerRow = AppStorage.local().getItem(PRICE_TAGS_PER_ROW)

        this.cellHeight = cellHeight || 115
        this.productsPerRow = productsPerRow || 4

        loadData('/admin/load/products/price-tags').then(data => {
            this.allProducts = data.products.map(p => new Product(p));
            this.totalPages = parseInt(this.allProducts.length / this.itemsPerPage) + 1
        });
    },

    data() {
        return {
            allProducts: [],
            filteredProducts: [],
            page: 1,
            itemsPerPage: 200,
            totalPages: 0,
            productsPerRow: 4,
            show: 'all',
            cellHeight: 115,
            currencySymbol: Money.currencySymbol
        }
    },

    watch: {
        show(val) {
            this.page = 1
            this.filteredProducts = []
        },

        cellHeight(val) {
            AppStorage.local().setItem(PRICE_TAGS_HEIGHT, val)
        },

        productsPerRow(val) {
            AppStorage.local().setItem(PRICE_TAGS_PER_ROW, val)
        }
    },

    computed: {
        products() {
            let filteredProducts = [];
            if(this.show === 'all') {
                filteredProducts = this.allProducts;
            } else if(this.show === 'custom') {
                filteredProducts = this.filteredProducts;
            } else if(this.show === 'recent') {
                filteredProducts = this.allProducts.filter(product => {
                    return product.modified_at && moment.utc(product.modified_at).isAfter(moment().add(-1, 'days'))
                });
            }

            const pageProducts = filteredProducts.slice((this.page - 1) * this.itemsPerPage, this.page * this.itemsPerPage)
            const products = []

            for(let i = 0; i <= parseInt(pageProducts.length / this.productsPerRow); i++ ) {
                const row = pageProducts.slice(i * this.productsPerRow, (i + 1) * this.productsPerRow)
                products.push(row)
            }

            return products
        }
    },

    methods: {
        print() {
            this.$printService.printHtml('price-tags')
        },

        goBack() {
            this.$router.push({ name: 'adminProducts' }).catch(err => {})
        },

        selectProduct(product) {
            // const alreadyAdded = this.filteredProducts.some(p => p.id == product.id)
            // if(alreadyAdded) return

            this.filteredProducts.push(product)
        },

        getPriceWoCents(priceString) {
            const decimalIndex = priceString.indexOf('.');
            return priceString.substring(0, decimalIndex);
        },

        getCents(priceString) {
            const decimalIndex = priceString.indexOf('.');
            return priceString.substring(decimalIndex + 1);
        }
    }
};
</script>

<style lang="scss" scoped>
.products-labels {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
    }

    .page-content {
        min-height: 200px;
        padding: 16px;

        .table-settings {
            display: flex;
            align-items: center;
            border-radius: 4px;
            margin: 8px 4px;
            background: #fafafa;
            border: 1px solid #b8b8b8;
        }
    }
}
</style>
